import { useState, type FormEvent } from 'react';
import { MailCheck, Send } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { ApiError, api } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';
import { PublicAuthLayout } from '@/components/layout/PublicAuthLayout';

/**
 * Demande de reinitialisation de mot de passe.
 *
 * L'API repond toujours 204, que l'adresse soit connue ou non : l'interface
 * affiche donc systematiquement le meme accuse de reception, sans jamais
 * confirmer l'existence d'un compte.
 */
export function ForgotPasswordPage() {
  const { t } = useLanguage();

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.localized() : t('errors.network'));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <PublicAuthLayout title={t('auth.forgot.sentTitle')}>
        <div className="space-y-4 rounded-2xl border border-border bg-surface p-5">
          <p className="flex items-start gap-3 text-sm text-ink">
            <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
            <span>{t('auth.forgot.sentBody')}</span>
          </p>
          <p className="text-xs text-ink-muted">{t('auth.forgot.sentHint')}</p>
          <Button variant="secondary" onClick={() => setSent(false)} className="w-full justify-center">
            {t('auth.forgot.retry')}
          </Button>
        </div>
      </PublicAuthLayout>
    );
  }

  return (
    <PublicAuthLayout title={t('auth.forgot.title')} description={t('auth.forgot.subtitle')}>
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-4" noValidate>
        <Field label={t('auth.email')} htmlFor="email" hint={t('auth.forgot.emailHint')} required>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={t('auth.emailPlaceholder')}
            invalid={Boolean(error)}
          />
        </Field>

        {error ? (
          <p role="alert" className="rounded-xl bg-danger-soft px-4 py-3 text-sm font-medium text-danger">
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          size="lg"
          loading={submitting}
          icon={<Send className="h-4 w-4" />}
          className="w-full justify-center"
        >
          {t('auth.forgot.submit')}
        </Button>
      </form>
    </PublicAuthLayout>
  );
}
